import { useMemo } from 'react'
import { View } from 'react-native'
import { PillButtonGroup } from '../../ui/PillButtonGroup'
import { SOUND_IDS } from '../../../src/shared/constants/sounds.constants'
import {
  VIEW_TYPE,
  FILTER_TYPE,
  type ViewType,
  type FilterType,
} from '../../../src/friends/constants/viewFilter.constants'

/** No-op handler: Groups view shows a single tab; PillButtonGroup still requires onChange. */
function noop(): void {
  return;
}

export interface FriendsFilterButtonsProps {
  filterType: FilterType
  counts: { friend: number; pending: number; suggested?: number }
  onFilterChange: (filter: FilterType) => void
  /** When GROUPS, only a single "Groups" label is shown (no Pending/Suggested tabs). */
  viewType?: ViewType
  /** Show the "Suggested" tab (Friends view, non-guest only). */
  showSuggested?: boolean
  testID?: string
}

export function FriendsFilterButtons({
  filterType,
  counts,
  onFilterChange,
  viewType = VIEW_TYPE.FRIENDS,
  showSuggested = false,
  testID,
}: FriendsFilterButtonsProps) {
  const groupsOptions = useMemo(
    () => [{ key: 'GROUPS' as const, label: `Groups (${counts.friend + counts.pending})` }],
    [counts.friend, counts.pending],
  )

  const friendsOptions = useMemo(
    () => [
      { key: FILTER_TYPE.FRIEND, label: `Active (${counts.friend})`, disabled: counts.friend === 0 },
      { key: FILTER_TYPE.PENDING, label: `Pending (${counts.pending})`, disabled: counts.pending === 0 },
      // Suggested stays enabled at 0 so users can open it and discover the empty-state CTA.
      ...(showSuggested
        ? [{ key: FILTER_TYPE.SUGGESTED, label: `Suggested (${counts.suggested ?? 0})` }]
        : []),
    ],
    [counts.friend, counts.pending, counts.suggested, showSuggested],
  )

  if (viewType === VIEW_TYPE.GROUPS) {
    return (
      <View testID={testID ?? 'friends-filter-buttons'}>
        <PillButtonGroup
          options={groupsOptions}
          value="GROUPS"
          onChange={noop}
          variant="social"
        />
      </View>
    )
  }

  return (
    <PillButtonGroup
      options={friendsOptions}
      value={filterType}
      onChange={onFilterChange}
      variant="social"
      soundId={SOUND_IDS.UI_TAP_SOCIAL}
      testID={testID ?? 'friends-filter-buttons'}
    />
  )
}
